import { Component, Injector, Input, OnInit } from '@angular/core';
import 'rxjs/add/operator/finally';
import { ChassisFormHandler } from '../../forms/form/classes/form-handler';
import { ActiveModal } from '../../modals/classes/active-modal';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { QuickAdditService } from '../services/quick-addit.service';
import { trans } from '../../localization/helpers';
import { ToastService } from '../../toast/services/toast.service';
import { ActiveModalButtons } from '../../modals/classes/active-modal-buttons';
import { strReplace, upperCaseFirst } from '../../support/helpers';
import { AlertService } from '../../alerts/services/alert.service';
import { LoaderService } from '../../support/loading/loader.service';
import { button, BUTTONS } from '../../support/buttons';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Model } from '../../api/model';
import { ApiService } from '../../api/api.service';
import { FormFactory } from '../../forms/services/form-factory.service';

@Component({
    selector: 'chassis-core-quick-addit-modal',
    templateUrl: 'quick-addit.modal.html',
    styleUrls: ['quick-addit.modal.scss'],
    providers: []
})
export class QuickAdditModal<T extends Model = Model> implements OnInit {

    public title = 'Quick Addit';

    /**
     * The Loading instance.
     */
    public loader: any;

    /**
     * The ChassisFormHandler instance.
     */
    public form: ChassisFormHandler;

    @Input() entity;

    public registeredEntity;

    protected apiService: ApiService;

    /**
     * Current step if multi stepper
     */
    public currentStep = 1;

    /**
     * Form structure to load in the form
     */
    public formStructure: Object = {};

    /**
     * The quick add it model.
     */
    public model: T;

    public model$ = new ReplaySubject<T>(1);

    /**
     * The model passed through the modal data.
     * When a parent is given the quick add it modal will execute it's
     * create actions when the parent is created.
     */
    protected parent: Observable<Model>;

    constructor(
        protected injector: Injector,
        protected toastService: ToastService,
        protected buttons: ActiveModalButtons,
        protected alertService: AlertService,
        protected modalButtons: ActiveModalButtons,
        protected loaderFactory: LoaderService,
        protected modal: ActiveModal,
        protected quickAddItService: QuickAdditService,
        protected formFactory: FormFactory,
    ) {
        //
    }

    public ngOnInit(): void {
        this.registeredEntity = this.quickAddItService.get(this.modal.get('entity'));
        this.apiService       = this.modal.get('service') || this.injector.get(this.registeredEntity.service);
        this.loader           = this.modal.get('loading');

        this.loadParent();

        if (!this.modal.get('entityId')) {
            this.formStructure = this.registeredEntity.getFormStructure();
            const newModel     = Object.assign(this.model || {}, this.registeredEntity.defaultProperties, this.modal.get('entityDefaults'));
            this.model         = this.apiService.make(newModel);
            this.model$.next(this.model);

            this.modal.set('title', this.getAddTitle());

        } else {
            this.formStructure = this.registeredEntity.getFormStructure(false);

            this.apiService.one(this.modal.get('entityId')).subscribe(model => {
                this.model = model;
                this.model$.next(this.model);
            });

            this.modal.set('title', this.getEditTitle());
        }

        this.createForm();
    }

    /**
     * Retrieve and set the parent from the modal data.
     *
     * @see The parent property for more information.
     */
    protected loadParent(): void {
        this.parent = this.modal
            .get<Observable<Model>>('parent');
    }

    private createForm(): void {
        if (this.registeredEntity.isMultiStepper()) {
            this.setupMultiStep();

            return;
        }

        this.form = this.formFactory.create(
            this.formStructure,
            this.model$,
            {
                name: 'quick-addit-' + this.modal.get('entity')
            }
        );

        this.updateModalSaveButton();

        // Add delete button if a existing entity is given
        if (this.modal.get('entityId')) {
            this.addModalDeleteButton();
        }
    }

    protected updateModalSaveButton(): void {
        const loader = this.loaderFactory.create();

        this.saveButtonFormSuccess(loader);
        this.saveButtonFormError(loader);

        this.modalButtons.update(
            'ok',
            {
                callback: () => {
                    this.loader.start();
                    loader.start();

                    if (this.parent) {
                        // Save the form when the parent is created.
                        this.parent.subscribe((parent) => {
                            if (!parent.exists()) {
                                return;
                            }

                            this.form.submit();
                        });
                    } else {
                        // Save the form instantly.
                        this.form.submit();
                    }
                },
                loader: () => {
                    return loader.isLoading();
                },
                disabled: () => {
                    return this.loader.isLoading();
                }
            });
    }

    protected saveButtonFormSuccess(loader): void {
        this.form.success(() => {
            this.loader.stop();
            loader.stop();

            this.modal.close(
                this.getResponse()
            );
        });

    }

    protected saveButtonFormError(loader): void {
        this.form.error(() => {
            this.loader.stop();
            loader.stop();
        });
    }

    protected addModalDeleteButton(): void {
        const loader = this.loaderFactory.create();

        this.modalButtons.add(
            BUTTONS.DELETE.NAME,
            button(
                BUTTONS.DELETE,
                {
                    callback: () => {
                        this.loader.start();
                        loader.start();

                        this.executeModalDeleteCallback()
                            .pipe(
                                finalize(() => {
                                    this.loader.stop();
                                    loader.stop();
                                })
                            )
                            .subscribe(() => {
                                this.modal.close({action: 'delete'});
                            });
                    },
                    loader: () => {
                        return loader.isLoading();
                    },
                    disabled: () => {
                        return this.loader.isLoading();
                    }
                }
            )
        );
    }

    protected executeModalDeleteCallback(): Observable<any> {
        return new Observable((observer) => {
            const prompt = this.alertService.open({
                title: trans('Are you sure?'),
            });

            prompt.result.then(() => {
                this.apiService
                    .delete(
                        this.modal.get('entityId')
                    )
                    .subscribe((result) => {
                        observer.next(result);
                        observer.complete();
                    }, (error) => {
                        observer.error(error);
                    });
            }, (error) => {
                observer.error(error);
            });
        });
    }

    private setupMultiStep(step = 1): void {
        this.currentStep = step;

        const stepCount    = this.registeredEntity.getSteps().length + 1;
        const stepObject   = this.registeredEntity.getSteps(step - 1);
        const buttons: any = this.modal.get('buttons');

        if (this.currentStep < stepCount) {
            // set multi step form with next button

            this.form = new ChassisFormHandler(this.injector, stepObject.structure, this.model$);

            buttons.get('ok').text     = trans('Next');
            buttons.get('ok').icon     = 'fas fa-arrow-right';
            buttons.get('ok').classes  = 'btn-primary';
            buttons.get('ok').callback = () => {
                this.form.validator.run(() => {

                    if (this.form.valid) {

                        if (stepObject.success) {

                            // if the step object has a callback -> resolve it
                            stepObject.success(this).then(() => {
                                this.setupMultiStep(this.currentStep + 1);
                            });

                        } else {

                            // when no callback is set -> go to the next step
                            this.setupMultiStep(this.currentStep + 1);

                        }

                    }

                }, error => {

                });
            };

        } else {
            // set last form with submit button
            this.form = new ChassisFormHandler(this.injector, this.formStructure, this.model$);

            buttons.get('ok').text     = trans('Save');
            buttons.get('ok').icon     = 'fas fa-check';
            buttons.get('ok').classes  = 'btn-success';
            buttons.get('ok').loader   = () => {
                return this.loader.isLoading();
            };
            buttons.get('ok').callback = () => {

                this.loader.start();

                this.form.success(() => {
                    this.loader.stop();

                    const response = this.getResponse();
                    this.modal.close(response);

                    return Promise.resolve();
                });

                this.form.error(() => {
                    this.loader.stop();
                });

                this.form.submit();

            };

        }

        if (this.currentStep > 1) {
            buttons.add('back', {
                text: trans('Back'),
                icon: 'fas fa-arrow-left',
                callback: () => {
                    this.setupMultiStep(this.currentStep - 1);
                }
            });
        } else {
            buttons.remove('back');
        }

    }

    protected getAddTitle(): string {
        return this.modal.get('title') || (trans('Add') + ' ' + trans(
            upperCaseFirst(strReplace(this.modal.get('entity'), '_', ' '))));
    }

    protected getEditTitle(): string {
        return this.modal.get('title') || (trans('Edit') + ' ' + trans(
            upperCaseFirst(strReplace(this.modal.get('entity'), '_', ' '))));
    }

    protected getResponse(): { [key: string]: any } {
        const data = {
            model: this.model,
            form: this.form,
            data: {
                model: this.model,
                form: this.form
            }
        };

        if (this.modal.get('action')) {
            data['action'] = this.modal.get('action');
        }

        return data;
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/quick-addit/modals/quick-addit.modal.ts